import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

export type UXStateType =
  | '404'
  | '403'
  | '500'
  | 'maintenance'
  | 'offline'
  | 'empty'
  | 'no_search_results'
  | 'loading'
  | 'error'
  | 'success'
  | 'session_expired';

export interface UXStateDetail {
  state: UXStateType;
  title: string;
  message: string;
  statusCode?: number;
  actionText?: string;
  actionType?: 'RETRY' | 'RELOGIN' | 'GO_HOME' | 'CLEAR_FILTERS' | 'CONTACT_SUPPORT' | 'DISMISS';
  contactEmail?: string;
  onAction?: () => void;
  metadata?: Record<string, any>;
}

interface UXStateContextValue {
  activeUX: UXStateDetail | null;
  isOffline: boolean;
  triggerUX: (state: UXStateType, overrides?: Partial<UXStateDetail>) => void;
  dismissUX: () => void;
  openAuthModal: () => void;
  registerAuthModalOpener: (fn: () => void) => void;
}

const DEFAULT_DETAILS: Record<UXStateType, Omit<UXStateDetail, 'state'>> = {
  '404': {
    title: 'Resource Not Found (404)',
    message: 'The match lobby, turf venue, or page you are looking for does not exist or has expired.',
    statusCode: 404,
    actionType: 'GO_HOME',
    actionText: 'Back to Match Radar',
  },
  '403': {
    title: 'Access Forbidden (403)',
    message: 'You do not have permission to join this private match, claim host controls, or edit this slot.',
    statusCode: 403,
    actionType: 'DISMISS',
    actionText: 'Dismiss',
  },
  '500': {
    title: 'Server Error (500)',
    message: 'Our database or match server encountered an unexpected error. Support has been notified at ashutosh4tech@gmail.com.',
    statusCode: 500,
    actionType: 'CONTACT_SUPPORT',
    actionText: 'Contact Support',
    contactEmail: 'ashutosh4tech@gmail.com',
  },
  'maintenance': {
    title: 'Scheduled System Maintenance',
    message: 'KhelDrop is undergoing scheduled system upgrades. Match radar and zero-cost lobbies will resume shortly.',
    statusCode: 503,
    actionType: 'RETRY',
    actionText: 'Check Status',
  },
  'offline': {
    title: 'Network Disconnected (Offline)',
    message: 'No internet connection detected. Match check-ins and live radar coordinates require an active connection.',
    actionType: 'RETRY',
    actionText: 'Try Reconnecting',
  },
  'empty': {
    title: 'No Matches Available (Empty State)',
    message: 'There are currently no open sports lobbies in your selected radius. Be the first to host one!',
    actionType: 'GO_HOME',
    actionText: 'Host a Game',
  },
  'no_search_results': {
    title: 'No Matching Games Found',
    message: 'No active games matched your current keyword or filter criteria. Try expanding your distance or clearing filters.',
    actionType: 'CLEAR_FILTERS',
    actionText: 'Reset Filters',
  },
  'loading': {
    title: 'Scanning Turf Radar...',
    message: 'Acquiring high-precision GPS coordinates and locating active games within 5 km...',
    actionType: 'DISMISS',
    actionText: 'Cancel Search',
  },
  'error': {
    title: 'Something Went Wrong',
    message: 'An unexpected application error occurred while processing your request. Please try again.',
    actionType: 'RETRY',
    actionText: 'Retry Action',
  },
  'success': {
    title: 'Action Completed Successfully',
    message: 'Your match action, slot reservation, or preferences were verified and saved.',
    actionType: 'DISMISS',
    actionText: 'Great, thanks!',
  },
  'session_expired': {
    title: 'Login Session Expired',
    message: 'Your authentication token has expired. Please sign in again to keep your spot in the match lobby.',
    statusCode: 401,
    actionType: 'RELOGIN',
    actionText: 'Sign In Again',
  },
};

const UXStateContext = createContext<UXStateContextValue | undefined>(undefined);

// Global subscriber for API interceptors outside React tree
let globalUXTrigger: ((state: UXStateType, overrides?: Partial<UXStateDetail>) => void) | null = null;

export function triggerGlobalUX(state: UXStateType, overrides?: Partial<UXStateDetail>) {
  if (globalUXTrigger) {
    globalUXTrigger(state, overrides);
  }
}

export function UXStateProvider({ children }: { children: ReactNode }) {
  const [activeUX, setActiveUX] = useState<UXStateDetail | null>(null);
  const [isOffline, setIsOffline] = useState<boolean>(
    typeof navigator !== 'undefined' ? !navigator.onLine : false
  );
  const [authModalOpener, setAuthModalOpener] = useState<(() => void) | null>(null);

  const triggerUX = useCallback((state: UXStateType, overrides?: Partial<UXStateDetail>) => {
    const base = DEFAULT_DETAILS[state];
    setActiveUX({
      state,
      ...base,
      ...overrides,
    });
  }, []);

  const dismissUX = useCallback(() => {
    setActiveUX(null);
  }, []);

  const registerAuthModalOpener = useCallback((fn: () => void) => {
    setAuthModalOpener(() => fn);
  }, []);

  const openAuthModal = useCallback(() => {
    dismissUX();
    if (authModalOpener) {
      authModalOpener();
    } else if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }, [authModalOpener, dismissUX]);

  // Sync global trigger
  useEffect(() => {
    globalUXTrigger = triggerUX;
    return () => {
      globalUXTrigger = null;
    };
  }, [triggerUX]);

  // Online / Offline detection
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleOnline = () => {
      setIsOffline(false);
      triggerUX('success', {
        title: 'Connection Restored',
        message: 'You are back online. Match radar and live sockets reconnected.',
      });
      setTimeout(() => {
        setActiveUX((prev) => (prev?.title === 'Connection Restored' ? null : prev));
      }, 4000);
    };

    const handleOffline = () => {
      setIsOffline(true);
      triggerUX('offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [triggerUX]);

  return (
    <UXStateContext.Provider
      value={{
        activeUX,
        isOffline,
        triggerUX,
        dismissUX,
        openAuthModal,
        registerAuthModalOpener,
      }}
    >
      {children}
    </UXStateContext.Provider>
  );
}

export function useUXState() {
  const context = useContext(UXStateContext);
  if (!context) {
    throw new Error('useUXState must be used within a UXStateProvider');
  }
  return context;
}
